import { type FC, useEffect, useState } from 'react';

import type {
  CarePlanViewModel,
  ScheduleSegmentVm,
  SetScheduleBody,
} from '@eluve/api-contract';
import { Button, Icon, Input, Stack, Tag, Text, cn } from '@eluve/components';
import { type MessageKey, useTranslation } from '@eluve/i18n';

const UNIT_LABEL: Record<string, MessageKey> = {
  d: 'carePlans.schedule.unitDays',
  wk: 'carePlans.schedule.unitWks',
  mo: 'carePlans.schedule.unitMo',
};

type Translate = ReturnType<typeof useTranslation>['t'];

const segmentLabel = (s: ScheduleSegmentVm, t: Translate): string => {
  const period = s.period ?? 1;
  const unitKey = UNIT_LABEL[s.periodUnit];
  const unit = unitKey ? t(unitKey) : s.periodUnit;
  const cadence =
    period === 1
      ? `${s.frequency}×/${unit}`
      : `${s.frequency}×/${period} ${unit}`;
  if (s.duration === null || s.durationUnit === null)
    return t('carePlans.schedule.cadenceForever', { cadence });
  const durationUnitKey = UNIT_LABEL[s.durationUnit];
  return t('carePlans.schedule.cadenceFor', {
    cadence,
    duration: s.duration,
    unit: durationUnitKey ? t(durationUnitKey) : s.durationUnit,
  });
};

const LADDER_WINDOW = 5;

export const ScheduleSection: FC<{
  schedule: CarePlanViewModel['schedule'];
  disabled?: boolean;
  onSetSchedule: (args: Omit<SetScheduleBody, 'appointmentId'>) => void;
}> = ({ schedule, disabled, onSetSchedule }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draftSegments, setDraftSegments] = useState<ScheduleSegmentVm[]>([]);
  const [reassessMode, setReassessMode] = useState<'visits' | 'days'>(
    schedule.reassess.afterDays !== null ? 'days' : 'visits',
  );
  const [reassessValue, setReassessValue] = useState<string>('');
  const [windowStart, setWindowStart] = useState(() => {
    const current = schedule.ladder.findIndex((s) => s.status === 'current');
    return Math.max(
      0,
      Math.min(current, schedule.ladder.length - LADDER_WINDOW),
    );
  });

  const updateSegment = (
    index: number,
    update: (segment: ScheduleSegmentVm) => ScheduleSegmentVm,
  ) =>
    setDraftSegments((segments) =>
      segments.map((segment, i) => (i === index ? update(segment) : segment)),
    );

  useEffect(() => {
    setWindowStart((start) =>
      Math.min(start, Math.max(0, schedule.ladder.length - LADDER_WINDOW)),
    );
  }, [schedule.ladder.length]);

  const startEditing = () => {
    setDraftSegments(
      schedule.segments.length > 0
        ? schedule.segments.map((s) => ({ ...s }))
        : [
            {
              frequency: 2,
              period: null,
              periodUnit: 'wk',
              duration: 2,
              durationUnit: 'wk',
            },
          ],
    );
    setReassessMode(schedule.reassess.afterDays !== null ? 'days' : 'visits');
    setReassessValue(
      String(
        schedule.reassess.afterVisits ?? schedule.reassess.afterDays ?? '',
      ),
    );
    setEditing(true);
  };

  const save = () => {
    const value = Number.parseInt(reassessValue, 10);
    const visitValue = Math.max(2, value);
    onSetSchedule({
      segments: draftSegments,
      reassess: {
        afterVisits:
          reassessMode === 'visits' && Number.isFinite(visitValue)
            ? visitValue
            : null,
        afterDays:
          reassessMode === 'days' && Number.isFinite(value) ? value : null,
      },
      reassessBooked: schedule.reassess.bookedManually,
    });
    setEditing(false);
  };

  const visibleSteps = schedule.ladder.slice(
    windowStart,
    windowStart + LADDER_WINDOW,
  );

  return (
    <section className="w-full rounded-xl border border-border-secondary bg-bg-primary p-4 shadow-sm">
      <Stack direction="horizontal" align="center" justify="between" gap={3}>
        <Text weight="semibold">{t('carePlans.schedule.title')}</Text>
        {!editing && (
          <Button
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={startEditing}
            aria-label={t('carePlans.schedule.editAriaLabel')}
          >
            <Icon name="Pencil" size="auto" />
          </Button>
        )}
      </Stack>

      {/* Summary + visit ladder stay visible whether or not the cadence
          editor is open (design keeps the ladder above the editor). */}
      <>
        <div className="mt-2">
          <Text weight="medium">
            {schedule.totalVisits !== null
              ? t('carePlans.schedule.visitsCount', {
                  count: schedule.totalVisits,
                })
              : schedule.segments.length > 0
                ? t('carePlans.schedule.ongoing')
                : t('carePlans.schedule.noCadence')}
          </Text>
          {schedule.segments.length > 0 && (
            <Text size="sm" color="secondary" className="block">
              {schedule.segments.map((s) => segmentLabel(s, t)).join(' → ')}
            </Text>
          )}
        </div>

        {schedule.ladder.length > 0 && (
          <Stack direction="horizontal" align="center" gap={2} className="mt-6">
            <Button
              size="sm"
              variant="ghost"
              disabled={windowStart === 0}
              onClick={() => setWindowStart((s) => Math.max(0, s - 1))}
              aria-label={t('carePlans.schedule.earlierVisits')}
            >
              <Icon name="ChevronLeft" size="auto" />
            </Button>
            <Stack
              direction="horizontal"
              align="start"
              justify="between"
              gap={2}
              className="flex-1"
            >
              {visibleSteps.map((step, index) => (
                <div
                  key={step.number}
                  className="relative flex min-w-0 flex-1 flex-col items-center gap-1.5 pt-7"
                >
                  {index < visibleSteps.length - 1 && (
                    <span
                      aria-hidden
                      className="absolute top-[43px] left-[calc(50%+20px)] h-px w-[calc(100%-40px)] bg-border-secondary"
                    />
                  )}
                  {step.reassess && (
                    <Tag
                      size="xs"
                      color="warning"
                      label={t('carePlans.schedule.reassessFlag')}
                      className="absolute top-0"
                    />
                  )}
                  <span
                    title={t('carePlans.schedule.visitStatusTitle', {
                      number: step.number,
                      status:
                        step.status === 'current'
                          ? t('carePlans.schedule.statusThisVisit')
                          : step.status === 'completed'
                            ? t('carePlans.schedule.statusCompleted')
                            : t('carePlans.schedule.statusUpcoming'),
                    })}
                    aria-current={
                      step.status === 'current' ? 'step' : undefined
                    }
                    className={cn(
                      'flex size-8 items-center justify-center rounded-full border text-sm',
                      step.status === 'current' &&
                        'border-border-insights-subtle bg-bg-insights-strong-primary text-text-insights-strong',
                      step.status === 'completed' &&
                        'border-border-secondary bg-bg-secondary text-text-secondary',
                      step.status === 'upcoming' &&
                        cn(
                          'border-border-secondary text-text-secondary',
                          step.reassess &&
                            'border-border-warning-subtle bg-bg-warning-subtle',
                        ),
                      step.booked && 'font-semibold',
                    )}
                  >
                    {step.status === 'completed' ? (
                      <Icon name="Check" size="xs" />
                    ) : (
                      step.number
                    )}
                  </span>
                  <Text size="xs" color="secondary">
                    {t('carePlans.visitNumber', { number: step.number })}
                  </Text>
                </div>
              ))}
            </Stack>
            <Button
              size="sm"
              variant="ghost"
              disabled={windowStart + LADDER_WINDOW >= schedule.ladder.length}
              onClick={() =>
                setWindowStart((s) =>
                  Math.min(s + 1, schedule.ladder.length - LADDER_WINDOW),
                )
              }
              aria-label={t('carePlans.schedule.laterVisits')}
            >
              <Icon name="ChevronRight" size="auto" />
            </Button>
          </Stack>
        )}
      </>

      {editing && (
        <Stack
          direction="vertical"
          gap={3}
          className="mt-4 border-t border-border-secondary pt-3"
        >
          <Text
            size="xs"
            weight="semibold"
            color="secondary"
            className="tracking-wide uppercase"
          >
            {t('carePlans.schedule.cadence')}
          </Text>
          {draftSegments.map((segment, i) => (
            <Stack key={i} direction="horizontal" align="center" gap={2} wrap>
              <Input
                type="number"
                min={1}
                value={segment.frequency}
                disabled={disabled}
                onChange={(e) =>
                  updateSegment(i, (s) => ({
                    ...s,
                    frequency: Number(e.target.value) || 1,
                  }))
                }
                className="w-16"
                aria-label={t('carePlans.schedule.segmentFrequencyAria', {
                  number: i + 1,
                })}
              />
              <Text size="sm">×/</Text>
              <select
                value={segment.periodUnit}
                disabled={disabled}
                onChange={(e) =>
                  updateSegment(i, (s) => ({
                    ...s,
                    periodUnit: e.target
                      .value as ScheduleSegmentVm['periodUnit'],
                  }))
                }
                className="rounded-md border border-border-secondary bg-bg-primary px-2 py-1.5 text-sm"
                aria-label={t('carePlans.schedule.segmentPeriodUnitAria', {
                  number: i + 1,
                })}
              >
                <option value="wk">{t('carePlans.schedule.perWeek')}</option>
                <option value="mo">{t('carePlans.schedule.perMonth')}</option>
              </select>
              <Text size="sm">{t('carePlans.schedule.for')}</Text>
              <Input
                type="number"
                min={1}
                value={segment.duration ?? ''}
                placeholder="∞"
                disabled={disabled}
                onChange={(e) => {
                  const n = Number.parseInt(e.target.value, 10);
                  updateSegment(i, (s) =>
                    Number.isFinite(n)
                      ? {
                          ...s,
                          duration: n,
                          durationUnit: s.durationUnit ?? s.periodUnit,
                        }
                      : { ...s, duration: null, durationUnit: null },
                  );
                }}
                className="w-16"
                aria-label={t('carePlans.schedule.segmentDurationAria', {
                  number: i + 1,
                })}
              />
              <select
                value={segment.durationUnit ?? 'forever'}
                disabled={disabled}
                onChange={(e) =>
                  updateSegment(i, (s) =>
                    e.target.value === 'forever'
                      ? { ...s, duration: null, durationUnit: null }
                      : {
                          ...s,
                          durationUnit: e.target.value as NonNullable<
                            ScheduleSegmentVm['durationUnit']
                          >,
                          duration: s.duration ?? 1,
                        },
                  )
                }
                className="rounded-md border border-border-secondary bg-bg-primary px-2 py-1.5 text-sm"
                aria-label={t('carePlans.schedule.segmentDurationUnitAria', {
                  number: i + 1,
                })}
              >
                <option value="wk">{t('carePlans.schedule.weeks')}</option>
                <option value="mo">{t('carePlans.schedule.months')}</option>
                <option value="d">{t('carePlans.schedule.days')}</option>
                <option value="forever">
                  {t('carePlans.schedule.forever')}
                </option>
              </select>
              <Button
                size="sm"
                variant="ghost"
                disabled={disabled || draftSegments.length === 1}
                onClick={() =>
                  setDraftSegments((prev) => prev.filter((_, j) => j !== i))
                }
                aria-label={t('carePlans.schedule.removeSegmentAria', {
                  number: i + 1,
                })}
              >
                <Icon name="Trash2" size="auto" />
              </Button>
            </Stack>
          ))}
          <Button
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={() =>
              setDraftSegments((prev) => [
                ...prev,
                {
                  frequency: 1,
                  period: null,
                  periodUnit: 'wk',
                  duration: null,
                  durationUnit: null,
                },
              ])
            }
            className="self-start"
          >
            <Icon name="Plus" size="auto" />
            {t('carePlans.schedule.addCadence')}
          </Button>

          <Stack
            direction="horizontal"
            align="center"
            gap={2}
            wrap
            className="border-t border-border-secondary pt-3"
          >
            <Text size="sm">{t('carePlans.schedule.reassessAfter')}</Text>
            <Input
              type="number"
              min={reassessMode === 'visits' ? 2 : 1}
              value={reassessValue}
              disabled={disabled}
              onChange={(e) => setReassessValue(e.target.value)}
              className="w-16"
              aria-label={t('carePlans.schedule.reassessAfter')}
            />
            <select
              value={reassessMode}
              disabled={disabled}
              onChange={(e) =>
                setReassessMode(e.target.value as 'visits' | 'days')
              }
              className="rounded-md border border-border-secondary bg-bg-primary px-2 py-1.5 text-sm"
              aria-label={t('carePlans.schedule.reassessUnitAria')}
            >
              <option value="visits">
                {t('carePlans.schedule.visitsOption')}
              </option>
              <option value="days">{t('carePlans.schedule.days')}</option>
            </select>
          </Stack>

          <Stack
            direction="horizontal"
            gap={2}
            justify="end"
            className="border-t border-border-secondary pt-3"
          >
            <Button
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => setEditing(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button size="sm" disabled={disabled} onClick={save}>
              {t('carePlans.review.saveChanges')}
            </Button>
          </Stack>
        </Stack>
      )}
    </section>
  );
};
